import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Search } from 'lucide-react';
import {
  listAgents, fetchChargers, fetchLocations,
  type AgentRow, type Charger, type Location,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { SalesPartnerConnectBanner } from '@/components/sp/SalesPartnerConnectBanner';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { formatRelativeTime } from '@/pages/dashboard/widgets/_lib';
import styles from './SalesPartnerMyChargersPage.module.css';

/**
 * Phase 7g.4b.4 Commit 1 , Sales Partner My Chargers page.
 *
 * Read-only listing of every charger assigned to the SP. Server-side
 * scoping at apps/api/src/routes/chargers/index.ts:283-286 already
 * filters /chargers to `{ agent_id: linkedAgentId }` for sales_agent
 * callers, so this page issues a vanilla fetchChargers() and gets
 * back only the SP's chargers (cross-tenant , chargers may live in
 * any host operator's tenant, the scope key is agent_id alone).
 *
 * Columns: Identity / Display name / Location / Commission /
 * Status / Last seen. The commission column shows per-charger
 * override (agent_commission_bps) when set, or a muted "Default
 * (X.X%)" pill sourced from agent.default_commission_bps when the
 * per-charger value is null. The override-vs-default distinction
 * is the most useful thing for an SP scanning their chargers
 * (it surfaces which sites they've negotiated custom rates for).
 *
 * Location names hydrate client-side via fetchLocations() (also
 * SP-scoped per Phase 7g.4a , returns only locations where the SP
 * has assigned chargers). One extra round-trip on mount in
 * exchange for not extending the /chargers projection on the
 * server.
 *
 * Out of scope by design: no add / edit / delete, no bulk
 * actions, no row click-through to a per-charger detail page. SP
 * has no operator powers over their chargers; this surface is
 * informational. Filed as a future enhancement if SPs ask for a
 * drill-down.
 *
 * Layout matches the SP Reports chrome (system-font headers,
 * inline-flow banners above the page header, max-width 1280
 * padded). The ResponsiveTable primitive carries the mobile
 * card-stack fallback so the table reads cleanly under 640px
 * without duplicate render paths.
 */

export function SalesPartnerMyChargersPage() {
  useRecentRoute('My Chargers');
  const user = useAuthStore((s) => s.user);
  const agentId = user?.agent_id ?? null;

  // Three queries on mount. All gate on agentId being non-null.
  // staleTime 30s matches the Dashboard cadence; the page is
  // low-traffic and a 30s stale window keeps tab-switch jank down.
  const agentsQ = useQuery({
    queryKey: ['sp-my-chargers', 'agents', agentId],
    queryFn: listAgents,
    enabled: !!agentId,
    staleTime: 30_000,
  });
  const chargersQ = useQuery({
    queryKey: ['sp-my-chargers', 'chargers', agentId],
    // limit:100 covers any realistic SP charger count in a single
    // page. The SP-scoped /chargers response per 7g.4a returns ONLY
    // chargers assigned to their Agent; pagination is YAGNI until
    // an SP grows past 100 chargers.
    queryFn: () => fetchChargers({ limit: 100 }),
    enabled: !!agentId,
    staleTime: 30_000,
  });
  const locationsQ = useQuery({
    queryKey: ['sp-my-chargers', 'locations', agentId],
    queryFn: () => fetchLocations({ limit: 500 }),
    enabled: !!agentId,
    staleTime: 30_000,
  });

  const agent: AgentRow | null = useMemo(() => {
    if (!agentsQ.data || !agentId) return null;
    return agentsQ.data.find((a) => a.id === agentId) ?? null;
  }, [agentsQ.data, agentId]);

  // Location-name lookup table. Built once per query change, keyed
  // by Location._id (the same id Charger.location_id references).
  const locationsById = useMemo(() => {
    const map = new Map<string, Location>();
    for (const l of locationsQ.data?.data ?? []) {
      map.set(l._id, l);
    }
    return map;
  }, [locationsQ.data]);

  // Filter state. Both filters are client-side so the user gets
  // instant feedback without re-firing the SP-scoped /chargers
  // query. Search matches identity OR display_name (case-
  // insensitive substring); location filter compares against the
  // raw location_id ref.
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState<string | null>(null);

  const allChargers = chargersQ.data?.data ?? [];
  const totalCount = chargersQ.data?.meta.total ?? allChargers.length;

  const filteredChargers = useMemo(() => {
    let rows = allChargers;
    if (locationFilter) {
      rows = rows.filter((c) => c.location_id === locationFilter);
    }
    const term = search.trim().toLowerCase();
    if (term) {
      rows = rows.filter((c) => {
        const id = c.identity.toLowerCase();
        const name = (c.display_name ?? '').toLowerCase();
        return id.includes(term) || name.includes(term);
      });
    }
    return rows;
  }, [allChargers, search, locationFilter]);

  // Profile-incomplete signal (replaces the old full-screen no-agent
  // early-return). The three queries above gate on agentId
  // (enabled: !!agentId) so they stay idle until provisioning lands;
  // the table shows its empty state and the SalesPartnerConnectBanner
  // surfaces the setup prompt as a non-blocking banner instead of
  // replacing the page.
  const profileIncomplete = (user?.is_sales_agent ?? false) && !agentId;

  const clearFilters = () => {
    setSearch('');
    setLocationFilter(null);
  };

  const filtersActive = !!search.trim() || !!locationFilter;
  const loading = chargersQ.isLoading || agentsQ.isLoading || locationsQ.isLoading;
  const fatalError = chargersQ.error
    ? (chargersQ.error instanceof Error ? chargersQ.error.message : 'Failed to load chargers')
    : null;

  return (
    <div className={styles.page}>
      <div className={styles.bannerStack}>
        <SalesPartnerConnectBanner
          agent={agent}
          agentId={agentId}
          profileIncomplete={profileIncomplete}
        />
      </div>

      <PageHeader />

      {fatalError && (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{fatalError}</span>
        </div>
      )}

      <div className={styles.controls}>
        <div className={styles.searchBar}>
          <Search size={14} className={styles.searchIcon} aria-hidden />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by charger ID or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search chargers"
          />
        </div>
        <select
          className={styles.locationSelect}
          value={locationFilter ?? ''}
          onChange={(e) => setLocationFilter(e.target.value === '' ? null : e.target.value)}
          aria-label="Location filter"
        >
          <option value="">All locations</option>
          {(locationsQ.data?.data ?? []).map((l) => (
            <option key={l._id} value={l._id}>{l.name}</option>
          ))}
        </select>
      </div>

      <p className={styles.countLine}>
        {loading && allChargers.length === 0
          ? 'Loading…'
          : `Showing ${filteredChargers.length.toLocaleString()} of ${totalCount.toLocaleString()} charger${totalCount === 1 ? '' : 's'}`}
      </p>

      <ChargersTable
        chargers={filteredChargers}
        locationsById={locationsById}
        loading={loading}
        totalCount={totalCount}
        filtersActive={filtersActive}
        onClearFilters={clearFilters}
      />
    </div>
  );
}

function PageHeader() {
  return (
    <header className={styles.header}>
      <h1 className={styles.title}>My Chargers</h1>
      <p className={styles.subtitle}>Chargers in your network.</p>
    </header>
  );
}

// ── Table ────────────────────────────────────────────────────────────

function ChargersTable({
  chargers, locationsById, loading, totalCount, filtersActive, onClearFilters,
}: {
  chargers: Charger[];
  locationsById: Map<string, Location>;
  loading: boolean;
  totalCount: number;
  filtersActive: boolean;
  onClearFilters: () => void;
}) {
  const columns: Column<Charger>[] = useMemo(() => ([
    {
      key: 'identity',
      header: 'Charger ID',
      cell: (c) => <code className={styles.codeCell}>{c.identity}</code>,
      sortable: true,
      sortKey: (c) => c.identity,
      mobile: 'primary',
    },
    {
      key: 'display_name',
      header: 'Name',
      cell: (c) => c.display_name
        ? <span>{c.display_name}</span>
        : <span className={styles.cellMuted}>Not available</span>,
      sortable: true,
      sortKey: (c) => c.display_name ?? '',
      mobile: 'secondary',
    },
    {
      key: 'location',
      header: 'Location',
      cell: (c) => {
        const loc = locationsById.get(c.location_id);
        return loc
          ? <span>{loc.name}</span>
          : <span className={styles.cellMuted}>Not available</span>;
      },
      sortable: true,
      sortKey: (c) => locationsById.get(c.location_id)?.name ?? '',
      mobile: 'body',
    },
    {
      key: 'commission',
      header: 'Commission',
      align: 'right',
      cell: (c) => <CommissionCell perCharger={c.agent_commission_bps ?? null} />,
      sortable: true,
      // Sort key collapses override and default to the same numeric
      // ladder so columns sort by effective rate.
      sortKey: (c) => {
        const v = c.agent_commission_bps ?? null;
        return v ?? 0;
      },
      mobile: 'body',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (c) => {
        // Never connected (no heartbeat ever): the SP install wizard
        // creates Charger docs BEFORE the hardware first connects, so
        // "registered but not yet online" is a waiting state, not an
        // outage. Distinct amber pill so the SP can tell "not plugged
        // in yet" from "went offline".
        if (c.is_online === false && !c.last_heartbeat) {
          return (
            <span className={`${styles.statusPill} ${styles.statusPending}`}>
              <span className={styles.statusDot} aria-hidden />
              Pending connection
            </span>
          );
        }
        return (
          <span className={`${styles.statusPill} ${c.is_online ? styles.statusOk : styles.statusOff}`}>
            <span className={styles.statusDot} aria-hidden />
            {c.is_online ? 'Online' : 'Offline'}
          </span>
        );
      },
      sortable: true,
      // is_online sorted: true first (descending puts online at top;
      // pending-connection rows sort with offline).
      sortKey: (c) => (c.is_online ? 1 : 0),
      mobile: 'body',
    },
    {
      key: 'last_seen',
      header: 'Last seen',
      align: 'right',
      cell: (c) => c.last_heartbeat
        ? <span className={styles.cellMeta}>{formatRelativeTime(c.last_heartbeat)}</span>
        : <span className={styles.cellMuted}>Not available</span>,
      sortable: true,
      // ISO strings sort correctly lexicographically when same
      // timezone; cast to Date to lean on the comparator's native
      // Date branch in ResponsiveTable.
      sortKey: (c) => c.last_heartbeat ? new Date(c.last_heartbeat) : new Date(0),
      mobile: 'body',
    },
  ]), [locationsById]);

  // Empty-state branches. Three states matter:
  //   1. Still loading + zero rows in cache → loading message
  //   2. Zero rows in SP scope at all → friendly first-time copy
  //   3. Zero rows after filters → "no matches" + Clear filters CTA
  if (loading && chargers.length === 0 && totalCount === 0) {
    return (
      <div className={styles.tableCard}>
        <p className={styles.emptyState}>Loading…</p>
      </div>
    );
  }
  if (totalCount === 0) {
    return (
      <div className={styles.tableCard}>
        <p className={styles.emptyState}>
          No chargers in your network yet. Once a building operator
          assigns a charger to you, it shows up here.
        </p>
      </div>
    );
  }
  if (chargers.length === 0 && filtersActive) {
    return (
      <div className={styles.tableCard}>
        <p className={styles.emptyState}>No chargers match your filters.</p>
        <div className={styles.emptyActions}>
          <button type="button" className={styles.clearBtn} onClick={onClearFilters}>
            Clear filters
          </button>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveTable<Charger>
      columns={columns}
      rows={chargers}
      rowKey={(c) => c._id}
      initialSortKey="identity"
      initialSortDir="asc"
    />
  );
}

// ── Commission cell ──────────────────────────────────────────────────
//
// Two visual modes: per-charger override (plain text), or default
// pill sourced from agent.default_commission_bps. The pill carries
// muted styling so the eye can scan the column and immediately
// distinguish overrides from defaults. Pattern source: the bps→%
// conversion at AgentFormModal.tsx:60 and AgentsListPage.tsx:256
// (divide by 100 to convert basis points → percent).

// In a partner's words (Warren, 28 October 2026: no developer copy): a point is one percent of each paid session, so
// the rate reads as a percent, and a charger without one yet gets its rate when it launches.
function CommissionCell({ perCharger }: { perCharger: number | null }) {
  return perCharger === null
    ? <span className={styles.cellMuted}>Set at launch</span>
    : <span className={styles.commissionOverride}>{Number((perCharger / 100).toFixed(2))}%</span>;
}
